/// <amd-module name="VSS/Features/Subscriptions/Components/SubscriptionPicker" />
import { ISubscriptionPickerProps } from "Components/SubscriptionPicker.Props";
import { Subscription } from "Models/Subscription";
import * as React from "react";
import * as SubscriptionResources from "VSS/Features/Subscriptions/Resources";
import { Button } from "VSSUI/Button";
import { IMessageBarProps, MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { css } from "VSSUI/Util";

export interface ISubscriptionPickerInternalState {
    currentlySubscriptionId?: string;
    selectedSubscriptionIsValid?: boolean;
    selectedSubscroptionIsValidPromise?: Promise<boolean>;
}

export class SubscriptionPicker extends React.Component<ISubscriptionPickerProps, ISubscriptionPickerInternalState>  {

    private get selectedSubscriptionId(): string {
        const { selectedSubscriptionId, defaultOrganizationLinkedSubscription } = this.props;
        const { organizationSubscription } = this.props.subscriptionContext.state;
        if (selectedSubscriptionId) {
            return selectedSubscriptionId;
        } else if (defaultOrganizationLinkedSubscription && organizationSubscription) {
            return organizationSubscription.id;
        } else {
            // do not return undefined so that radio group selects non-first element
            return "noneselected";
        }
    }

    public static getDerivedStateFromProps(
        nextProps: Readonly<ISubscriptionPickerProps>,
        prevState: Readonly<ISubscriptionPickerInternalState>): Partial<ISubscriptionPickerInternalState> | null {
        const { selectedSubscriptionId, validateSubscriptionChoices } = nextProps;
        const { currentlySubscriptionId } = prevState;
        if (selectedSubscriptionId !== currentlySubscriptionId) {
            const validateSubscriptionPromise = validateSubscriptionChoices
                ? SubscriptionPicker.getValidateSubscriptionPromise(nextProps)
                : undefined;
            return {
                currentlySubscriptionId: selectedSubscriptionId,
                selectedSubscroptionIsValidPromise: validateSubscriptionPromise,
                selectedSubscriptionIsValid: undefined,
            };
        }

        return null;
    }

    private static subscriptionIdToIsValidPromise: { [subscriptionId: string]: Promise<boolean> } = {};

    private static getValidateSubscriptionPromise(props: ISubscriptionPickerProps): Promise<boolean> {
        const { organizationSubscription } = props.subscriptionContext.state;
        const { hostId, selectedSubscriptionId } = props;
        const { validateSubscription } = props.subscriptionContext.actions;
        if (!selectedSubscriptionId || (organizationSubscription && selectedSubscriptionId === organizationSubscription.id)) {
            return Promise.resolve(true);
        } else {
            const validatePromise = this.subscriptionIdToIsValidPromise[selectedSubscriptionId] || validateSubscription(selectedSubscriptionId, hostId);
            this.subscriptionIdToIsValidPromise[selectedSubscriptionId] = validatePromise;
            return validatePromise;
        }
    }
    private mounted: boolean = false;

    constructor(props: ISubscriptionPickerProps, context?: any) {
        super(props, context);
        this.state = {};
    }

    public async componentDidUpdate() {
        const {
            selectedSubscroptionIsValidPromise: validatePromise,
            currentlySubscriptionId,
        } = this.state;
        const { subscriptionIdToSubscription } = this.props.subscriptionContext.state;
        if (currentlySubscriptionId && validatePromise) {
            // clear so we don't check many times
            this.setState({
                selectedSubscroptionIsValidPromise: undefined,
            });
            const isValid = await validatePromise;
            if (this.props.onSubscriptionValidated) {
                this.props.onSubscriptionValidated(currentlySubscriptionId, isValid);
            }
            if (this.mounted) {
                this.setState((prevState) => {
                    if (prevState.currentlySubscriptionId === this.state.currentlySubscriptionId) {
                        return {
                            selectedSubscriptionIsValid: isValid,
                        };
                    }
                    return null;
                });
            }
        }
    }

    public componentDidMount() {
        this.mounted = true;
        this.componentDidUpdate();
    }

    public componentWillUnmount() {
        this.mounted = false;
    }

    public render(): JSX.Element | null {
        const { className } = this.props;
        return (
            <div className={css("flex-column flex-noshrink", className)}>
                <div className="subscription-picker-title">{SubscriptionResources.SubscriptionPickerTitle}</div>
                {this.renderSubscriptionChoiceGroup()}
                {this.renderCreateSubscriptionButton()}
            </div>);
    }

    private renderSubscriptionChoiceGroup() {
        const { loadedAllSubscriptions, subscriptions } = this.props.subscriptionContext.state;
        if (!loadedAllSubscriptions || !subscriptions) {
            return (
                <div className="subscription-picker-spinner">
                    <Spinner size={SpinnerSize.medium} />
                    <div>{SubscriptionResources.LoadingSubscriptions}</div>
                </div>);
        } else if (subscriptions.length === 0) {
            return <div>{SubscriptionResources.NoAzureSubscriptionsMessage}</div>;
        } else {
            return (
                <div className="flex-column flex-noshrink">
                    <div className="subscription-picker-choiceGroupContainer">
                        <RadioButtonGroup
                            selectedButtonId={this.selectedSubscriptionId}
                            onSelect={this.onChoiceGroupChange}
                        >
                            {subscriptions.map((subscription: Subscription) => {
                                const { id, isUsable, displayName } = subscription;
                                const radioButtonAriaLabelId = `subscription-picker-${id}`;
                                return <RadioButton
                                    id={id}
                                    key={`radioButton-${id}-key`}
                                    ariaLabelledBy={radioButtonAriaLabelId}
                                    disabled={!isUsable}
                                >
                                    <div className="bolt-radio-button-label" id={radioButtonAriaLabelId}>
                                        {displayName}
                                        <span className="subscription-picker-radiobuttonsublabel">{id}</span>
                                    </div>
                                </RadioButton>;
                            })}
                        </RadioButtonGroup>
                    </div>
                    {this.renderValidationMessageBar()}
                </div>);
        }
    }

    private renderValidationMessageBar(): JSX.Element | null {
        const { validateSubscriptionChoices } = this.props;
        const { selectedSubscriptionIsValid } = this.state;
        const { subscriptionIdToSubscription } = this.props.subscriptionContext.state;

        if (!validateSubscriptionChoices) {
            return null;
        }

        const subscription = this.selectedSubscriptionId && subscriptionIdToSubscription
            ? subscriptionIdToSubscription[this.selectedSubscriptionId]
            : undefined;

        // calculate message to show based on loading, waiting for input, valid and invalid
        const messageBarProps: IMessageBarProps = {};
        let showSpinner: boolean = false;
        let messageBarMessage: string = "";
        if (subscription) {
            if (selectedSubscriptionIsValid === undefined) {
                // validating subscription
                showSpinner = true;
                messageBarProps.severity = MessageBarSeverity.Info;
                messageBarMessage = SubscriptionResources.ValidatingSubscription;
            } else if (selectedSubscriptionIsValid) {
                // subscription is valid
                messageBarProps.severity = MessageBarSeverity.Success;
                messageBarMessage = SubscriptionResources.SubscriptionValid;
            } else {
                // subscription is invalid
                messageBarProps.severity = MessageBarSeverity.Error;
                messageBarMessage = SubscriptionResources.SubscriptionInvalid + ". " + subscription.invalidReason;
            }
        } else {
            // waiting for selection
            messageBarProps.severity = MessageBarSeverity.Info;
            messageBarMessage = SubscriptionResources.WaitingToValidate;
        }

        return (
            <div className="subscription-picker-messageBarContainer">
                <MessageBar {...messageBarProps}>
                    {this.renderMessageBarContent(messageBarMessage, showSpinner)}
                </MessageBar>
            </div>);
    }

    private renderMessageBarContent(message: string, spinner: boolean): JSX.Element {
        return (
            <div className="subscription-picker-messageBarContent flex-row flex-noshrink">
                {spinner && <Spinner size={SpinnerSize.small} />}
                <div> {message} </div>
            </div>);
    }

    private renderCreateSubscriptionButton() {
        if (!this.props.showCreateSubscriptionButton) {
            return undefined;
        }

        return (
            <div className="subscription-picker-create-button-container">
                <Button
                    ariaLabel={SubscriptionResources.NewAzureSubscriptionAriaLabel}
                    text={SubscriptionResources.NewAzureSubscription}
                    onClick={this.onCreateSubscriptionButtonClick}
                    iconProps={{ iconName: "Add" }}
                />
            </div>);
    }

    private onCreateSubscriptionButtonClick = () => {
        const { createSubscriptionReplyTo, createSubscriptionReplyToQueryParams } = this.props;
        const { redirectToCreateSubscription } = this.props.subscriptionContext.actions;
        redirectToCreateSubscription(createSubscriptionReplyTo, createSubscriptionReplyToQueryParams);
    }

    private onChoiceGroupChange = async (subscriptionId: string): Promise<void> => {
        const { subscriptionIdToSubscription } = this.props.subscriptionContext.state;
        this.props.onSubscriptionChoiceChanged(subscriptionIdToSubscription[subscriptionId]);
    }
}
